import React, { useState } from 'react';
import ReactCodeInput from 'react-code-input';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Link,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  Card,
  CardMedia,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  useTheme,
  Autocomplete,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import countryCodesData from '../../data/countryCodes.json';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import config from '../../config.js';

const countryCodes = countryCodesData.countryCodes;

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 600,
  margin: '20px auto',
  borderRadius: 16,
  boxShadow: 'none',
  [theme.breakpoints.down('sm')]: {
    margin: '10px',
    padding: theme.spacing(2),
  },
}));

const Form = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  height: 56,
  borderRadius: 50,
  fontSize: '1.1rem',
  textTransform: 'none',
  opacity: 1,
  '&.Mui-disabled': {
    backgroundColor: theme.palette.primary.disabled,
    color: theme.palette.primary.contrastText,
    opacity: 1,
  },
  [theme.breakpoints.down('sm')]: {
    height: 56,
  },
}));

const StyleCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  borderRadius: 12,
  transition: 'transform 0.2s',
  border: 'none',
  backgroundColor: theme.palette.primary.brandLight,
  color: theme.palette.primary.main,
  '&:hover': {
    transform: 'scale(1.02)',
    backgroundColor: theme.palette.primary.light,
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    '& .MuiTypography-root': {
      color: '#fff',
    },
    '& .MuiCardContent-root': {
      backgroundColor: 'transparent',
    }
  },
  '& .MuiCardContent-root': {
    backgroundColor: 'transparent',
  }
}));

const GenderButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: 120,
  borderRadius: 16,
  fontSize: '1.4rem',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  transition: 'all 0.3s ease',
  border: '2px solid',
  '& .MuiSvgIcon-root': {
    fontSize: '3.5rem',
  },
  '&.woman': {
    borderColor: '#D23F57',
    color: '#D23F57',
    backgroundColor: 'transparent',
    '& .MuiSvgIcon-root': {
      color: '#D23F57',
    },
    '&:hover': {
      backgroundColor: 'rgba(210, 63, 87, 0.04)',
    },
    '&.selected': {
      backgroundColor: '#D23F57',
      color: '#FFF',
      '& .MuiSvgIcon-root': {
        color: '#FFF',
      },
    },
  },
  '&.man': {
    borderColor: '#2196F3',
    color: '#2196F3',
    backgroundColor: 'transparent',
    '& .MuiSvgIcon-root': {
      color: '#2196F3',
    },
    '&:hover': {
      backgroundColor: 'rgba(33, 150, 243, 0.04)',
    },
    '&.selected': {
      backgroundColor: '#2196F3',
      color: '#FFF',
      '& .MuiSvgIcon-root': {
        color: '#FFF',
      },
    },
  },
}));

const AgeInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    fontSize: '1.2rem',
  },
  '& input': {
    textAlign: 'center',
    fontSize: '2rem',
    padding: theme.spacing(2),
  },
}));

const SizeToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiToggleButton-root': {
    borderRadius: 20,
    padding: '8px 24px',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

const NotSureButton = styled(Button)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: 12,
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
  },
  '&.selected': {
    backgroundColor: theme.palette.grey[300],
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
}));

const ColorButton = styled(Button)(({ color, theme }) => ({
  minWidth: '80px',
  height: '40px',
  margin: theme.spacing(0.5),
  borderRadius: 8,
  textTransform: 'none',
  backgroundColor: color.toLowerCase(),
  color: getContrastText(color.toLowerCase()),
  border: `1px solid ${color === 'White' ? '#e0e0e0' : 'transparent'}`,
  '&:hover': {
    backgroundColor: color.toLowerCase(),
    opacity: 0.9,
  },
  '&.selected': {
    border: `3px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 10px ${color === 'White' ? '#e0e0e0' : color.toLowerCase()}`,
  },
}));

const ProgressBar = styled(Box)(({ theme, isactive, iscompleted }) => ({
  height: 4,
  width: '100%',
  backgroundColor: theme.palette.divider,
  borderRadius: 2,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: iscompleted ? '100%' : isactive ? 'var(--progress)' : '0%',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.3s ease',
  }
}));

const CountryCodeField = styled(Autocomplete)(({ theme }) => ({
  width: '30%',
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: 'white',
    height: '56px',
    display: 'flex',
    alignItems: 'center'
  },
  '& .MuiAutocomplete-input': {
    padding: '4px 8px !important',
    height: '1.4375em !important'
  },
  '& .MuiAutocomplete-endAdornment': {
    display: 'none'
  }
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  gap: theme.spacing(2),
  '& .MuiToggleButtonGroup-grouped': {
    margin: 0,
    border: '2px solid',
    borderColor: theme.palette.grey[300],
    borderRadius: '12px !important',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    backgroundColor: 'white',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      borderColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  width: '50%',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 500,
  height: '56px',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      color: theme.palette.primary.main,
    },
  },
}));

const GenderOption = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '56px',
  borderRadius: '12px',
  fontSize: '1.2rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.brandLight,
  border: 'none',
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
  },
  '& .circle': {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: `2px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '& .circle': {
      border: 'none',
      '& .MuiSvgIcon-root': {
        color: '#fff',
        fontSize: '24px'
      }
    }
  },
  '&:hover': {
      backgroundColor: theme.palette.primary.brandLight,
    '&.selected': {
      backgroundColor: theme.palette.primary.main,
    }
  }
}));

const outfitOptions = {
  men: [
    {
      id: 1,
      image: '/outfits/male/casual_cool.jpg',
      style: 'Casual Cool',
      description: 'Everyday comfort with a trendy twist'
    },
    {
      id: 2,
      image: '/outfits/male/minimalist.webp',
      style: 'Modern Minimal',
      description: 'Clean lines and sophisticated simplicity'
    },
    {
      id: 3,
      image: '/outfits/male/street.jpg',
      style: 'Street Style',
      description: 'Bold, urban-inspired looks'
    },
    {
      id: 4,
      image: '/outfits/male/classic.jpg',
      style: 'Classic',
      description: 'Timeless and polished essentials'
    },
    {
      id: 5,
      image: '/outfits/male/trendy.png',
      style: 'Trendy',
      description: 'Latest fashion-forward pieces'
    }
  ],
  women: [
    {
      id: 1,
      image: '/outfits/female/casual_cool.jpg',
      style: 'Casual Cool',
      description: 'Everyday comfort with a trendy twist'
    },
    {
      id: 2,
      image: '/outfits/female/minimalist.webp',
      style: 'Modern Minimal',
      description: 'Clean lines and sophisticated simplicity'
    },
    {
      id: 3,
      image: '/outfits/female/street.jpg',
      style: 'Street Style',
      description: 'Bold, urban-inspired looks'
    },
    {
      id: 4,
      image: '/outfits/female/classic.jpg',
      style: 'Classic',
      description: 'Timeless and polished essentials'
    },
    {
      id: 5,
      image: '/outfits/female/trendy.png',
      style: 'Trendy',
      description: 'Latest fashion-forward pieces'
    }
  ]
};

const OutfitCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 400,
  margin: '0 auto',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  borderRadius: 0,
}));

const OutfitImage = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '250px',
  height: '400px',
  margin: '0 auto',
  overflow: 'hidden',
  borderRadius: '16px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease',
  backgroundColor: theme.palette.grey[100],
  '&:hover': {
    transform: 'scale(1.02)',
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
    transition: 'transform 0.3s ease',
  }
}));

const StyleActionButton = styled(Button)(({ theme }) => ({
  width: '76.5px',
  height: '160px',
  backgroundColor: theme.palette.primary.brandLight,
  borderRadius: '12px',
  border: 'none',
  minWidth: 'unset',
  padding: 0,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    border: 'none',
  },
  fontSize: '24px'
}));

// Helper function to determine text color based on background
const getContrastText = (hexColor) => {
  // Convert hex to RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate relative luminance
  // Using the formula from WCAG 2.0
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black for light colors, white for dark ones
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

const colors = [
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#ffffff' },
  { name: 'Navy', value: '#000080' },
  { name: 'Royal Blue', value: '#4169e1' },
  { name: 'Red', value: '#dc143c' },
  { name: 'Forest Green', value: '#228b22' },
  { name: 'Hot Pink', value: '#ff69b4' },
  { name: 'Deep Purple', value: '#483d8b' },
  { name: 'Chocolate', value: '#8b4513' },
  { name: 'Charcoal', value: '#36454f' },
  { name: 'Beige', value: '#f5f5dc' },
  { name: 'Burgundy', value: '#800020' },
  { name: 'Olive', value: '#556b2f' },
  { name: 'Teal', value: '#008080' },
  { name: 'Lavender', value: '#e6e6fa' },
  { name: 'Gold', value: '#ffd700' },
  { name: 'Silver', value: '#c0c0c0' },
  { name: 'Coral', value: '#ff7f50' }
];

const steps = ['Welcome', 'Name', 'Contact', 'Verification', 'Gender', 'Age', 'Sizes', 'Style', 'Final Welcome'];

const sizeOptions = {
  men: {
    tops: {
      us: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'],
      eu: ['44', '46', '48', '50', '52', '54', '56', '58']
    },
    bottoms: {
      waist: {
        us: ['28', '30', '32', '34', '36', '38', '40', '42', '44'],
        eu: ['44', '46', '48', '50', '52', '54', '56', '58', '60']
      },
      length: ['28', '30', '32', '34', '36', '38']
    },
    suits: {
      us: ['36S', '36R', '36L', '38S', '38R', '38L', '40S', '40R', '40L', '42S', '42R', '42L', '44R', '44L', '46R', '46L'],
      eu: ['44', '46', '48', '50', '52', '54', '56', '58']
    },
    shoes: {
      us: ['6', '7', '8', '9', '10', '11', '12', '13', '14'],
      eu: ['39', '40', '41', '42', '43', '44', '45', '46', '47']
    }
  },
  women: {
    tops: {
      us: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'],
      eu: ['32', '34', '36', '38', '40', '42', '44']
    },
    dresses: {
      us: ['0', '2', '4', '6', '8', '10', '12', '14', '16'],
      eu: ['32', '34', '36', '38', '40', '42', '44', '46', '48']
    },
    bottoms: {
      waist: {
        us: ['23', '24', '25', '26', '27', '28', '29', '30', '31', '32'],
        eu: ['32', '34', '36', '38', '40', '42', '44', '46']
      },
      length: ['26', '28', '30', '32', '34']
    },
    suits: {
      us: ['0', '2', '4', '6', '8', '10', '12', '14'],
      eu: ['32', '34', '36', '38', '40', '42', '44', '46']
    },
    shoes: {
      us: ['5', '6', '7', '8', '9', '10', '11'],
      eu: ['35', '36', '37', '38', '39', '40', '41', '42']
    }
  }
};

const clothingTypes = {
  men: [
    { id: 'tops', label: 'Tops / T-Shirts', format: 'letter' },
    { id: 'suits', label: 'Suits & Blazers', format: 'number' },
    { id: 'bottoms', label: 'Pants / Jeans', format: 'waist-length' },
    { id: 'shoes', label: 'Shoes', format: 'number' }
  ],
  women: [
    { id: 'tops', label: 'Tops / T-Shirts', format: 'letter' },
    { id: 'dresses', label: 'Dresses', format: 'number' },
    { id: 'suits', label: 'Suits & Blazers', format: 'number' },
    { id: 'bottoms', label: 'Pants / Jeans', format: 'waist-length' },
    { id: 'shoes', label: 'Shoes', format: 'number' }
  ]
};

const styleOptions = [
  {
    id: 1,
    name: 'Casual Cool',
    image: '/styles/casual.jpg',
    description: 'Everyday comfort with a trendy twist'
  },
  {
    id: 2,
    name: 'Modern Minimal',
    image: '/styles/minimal.jpg',
    description: 'Clean lines and sophisticated simplicity'
  },
  {
    id: 3,
    name: 'Street Style',
    image: '/styles/street.jpg',
    description: 'Bold, urban-inspired looks'
  },
  {
    id: 4,
    name: 'Classic',
    image: '/styles/classic.jpg',
    description: 'Timeless and polished essentials'
  },
  {
    id: 5,
    name: 'Trendy',
    image: '/styles/trendy.jpg',
    description: 'Latest fashion-forward pieces'
  },
  {
    id: 6,
    name: 'Modest Fashion',
    image: '/styles/modest.jpg',
    description: 'Elegant and conservative styles with full coverage'
  },
  {
    id: 7,
    name: 'Athleisure',
    image: '/styles/athleisure.jpg',
    description: 'Athletic wear that transitions from gym to street'
  },
  {
    id: 8,
    name: 'Bohemian',
    image: '/styles/bohemian.jpg',
    description: 'Free-spirited, artistic, and layered looks'
  },
  {
    id: 9,
    name: 'Business Professional',
    image: '/styles/business.jpg',
    description: 'Polished workwear and office attire'
  },
  {
    id: 10,
    name: 'Edgy',
    image: '/styles/edgy.jpg',
    description: 'Bold, alternative, and statement-making pieces'
  },
  {
    id: 11,
    name: 'Eco-Conscious',
    image: '/styles/sustainable.jpg',
    description: 'Sustainable and ethically made fashion'
  },
  {
    id: 12,
    name: 'Vintage Inspired',
    image: '/styles/vintage.jpg',
    description: 'Retro looks with a modern touch'
  },
  {
    id: 13,
    name: 'Preppy',
    image: '/styles/preppy.jpg',
    description: 'Clean-cut, collegiate-inspired style'
  },
  {
    id: 14,
    name: 'Streetwear',
    image: '/styles/streetwear.jpg',
    description: 'Urban culture-inspired casual wear'
  },
  {
    id: 15,
    name: 'Fusion',
    image: '/styles/fusion.jpg',
    description: 'Modern mix of cultural and contemporary styles'
  },
  {
    id: 16,
    name: 'Minimalist Plus',
    image: '/styles/minimalist-plus.jpg',
    description: 'Clean, modern cuts in inclusive sizing'
  },
  {
    id: 17,
    name: 'Adaptive Fashion',
    image: '/styles/adaptive.jpg',
    description: 'Stylish clothing with accessible design features'
  },
  {
    id: 18,
    name: 'Future Fashion',
    image: '/styles/future.jpg',
    description: 'Futuristic and avant-garde styles'
  }
];

// Replace SwipeableViews implementation with a more modern approach using the MUI Tabs
function TabPanel(props) {
  const { children, value, index, sx = {}, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-tabpanel-${index}`}
      aria-labelledby={`contact-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ mb: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StyledDateField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: 'white',
    height: '56px',
    fontSize: '1.2rem',
    textAlign: 'center',
    '& input': {
      textAlign: 'center',
      padding: '14px',
    }
  }
}));

function Register({ onToggleAuth }) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sizeSystem, setSizeSystem] = useState('us');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    gender: '',
    age: '',
    sizes: {},
    selectedStyles: [],
    favoriteColors: [],
  });
  
  const { register, setCurrentUser } = useAuth();
  const navigate = useNavigate();

  const [emailStatus, setEmailStatus] = useState({
    checking: false,
    available: true,
    message: ''
  });

  const [currentOutfitIndex, setCurrentOutfitIndex] = useState(0);
  const [outfitResponses, setOutfitResponses] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [dateError, setDateError] = useState('');

  const handleOutfitResponse = (liked) => {
    if (!formData.gender) return;
    
    const currentOutfit = outfitOptions[formData.gender][currentOutfitIndex];
    const isLastOutfit = currentOutfitIndex + 1 >= outfitOptions[formData.gender].length;
    
    // Create new response
    const newResponse = {
      outfitId: currentOutfit.id,
      style: currentOutfit.style,
      liked
    };
    
    // Update responses
    const updatedResponses = [...outfitResponses, newResponse];
    setOutfitResponses(updatedResponses);
    
    // If this was the last outfit, update selected styles
    if (isLastOutfit) {
      // Get unique liked styles
      const likedStyles = [...new Set(
        updatedResponses
          .filter(response => response.liked)
          .map(response => response.style)
      )];
      
      setFormData(prevData => ({
        ...prevData,
        selectedStyles: likedStyles
      }));
      
      // Move to next step after a short delay
      setTimeout(() => handleNext(), 300);
    } else {
      // Update current outfit index only if not the last outfit
      setCurrentOutfitIndex(prev => prev + 1);
    }
  };

  // Helper function to validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const checkEmail = async (email) => {
    if (!email) {
      setEmailStatus({
        checking: false,
        available: false,
        message: 'Email is required'
      });
      return;
    }

    if (!isValidEmail(email)) {
      setEmailStatus({
        checking: false,
        available: false,
        message: 'Please enter a valid email address'
      });
      return;
    }
    
    try {
      setEmailStatus(prev => ({ ...prev, checking: true, message: '' }));
      const response = await fetch(`${config.apiBaseUrl}/api/auth/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          send_code: false
        })
      });
      
      const data = await response.json();
      
      // Handle both success and error cases
      setEmailStatus({
        checking: false,
        available: data.available !== false, // if available is false, email is taken
        message: data.message || (data.available === false ? 'This email is already registered' : '')
      });
      
    } catch (error) {
      console.error('Email check error:', error);
      setEmailStatus({
        checking: false,
        available: false,
        message: 'Server error. Please try again later.'
      });
    }
  };

  // Modify the debouncedCheck
  const debouncedCheck = React.useCallback(
    debounce((value, type) => {
      if (value) {
        if (type === 'email') {
          checkEmail(value);
        }
      }
    }, 500),
    []
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'email' && value) {
      debouncedCheck(value, 'email');
    }
  };

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendVerificationCode = async () => {
    setError('');
    setLoading(true);
    
    try {
      if (!formData.email.trim() || !formData.email.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }

      // Send verification code instead of registering
      const response = await fetch(`${config.apiBaseUrl}/api/auth/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          send_code: true
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        setCodeSent(true);
        startCountdown();
        setActiveStep(3); // Advance to verification step
      } else {
        const errorMessage = data.message || 'Failed to send verification code. Please try again.';
        console.error('Verification error:', errorMessage);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);
    
    try {
      if (!verificationCode.trim()) {
        setError('Please enter the verification code');
        return;
      }

      const response = await fetch(`${config.apiBaseUrl}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          code: verificationCode,
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        // Store temporary tokens for later use in final registration
        localStorage.setItem('temp_access_token', data.access_token);
        localStorage.setItem('temp_refresh_token', data.refresh_token);
        
        // Move to the next step (Gender selection)
        setActiveStep(4);
      } else {
        setError(data.message || 'Invalid verification code. Please try again.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // First validate the current step
    if (!validateStep()) {
      return;
    }

    // Special handling for verification steps
    if (activeStep === 2) {
      await handleSendVerificationCode();
      return;
    }
    
    if (activeStep === 3) {
      if (!verificationCode.trim()) {
        setError('Please enter the verification code');
        return;
      }
      await handleVerifyCode();
      return;
    }

    // For step 7 (style), allow skipping
    if (activeStep === 7) {
      if (formData.gender && currentOutfitIndex < outfitOptions[formData.gender].length - 1) {
        // Don't show error, just proceed with current selections
        const likedStyles = [...new Set(
          outfitResponses
            .filter(response => response.liked)
            .map(response => response.style)
        )];
        
        setFormData(prev => ({
          ...prev,
          selectedStyles: likedStyles
        }));
      }
    }

    // For step 5 (age), ensure age is valid
    if (activeStep === 5) {
      if (!formData.birthDate) {
        setError('Please select your birth date');
        return;
      }
      const age = dayjs().diff(dayjs(formData.birthDate), 'year');
      if (age < 13) {
        setError('You must be at least 13 years old to use Lyke');
        return;
      }
    }

    // For step 6 (sizes), ensure all required sizes are selected
    if (activeStep === 6) {
      const clothingTypesForGender = clothingTypes[formData.gender] || [];
      const allSizesSelected = clothingTypesForGender.every(type => {
        if (type.id === 'bottoms') {
          return (formData.sizes.bottoms?.waist === 'not_sure') || 
                 (formData.sizes.bottoms?.waist && formData.sizes.bottoms?.length);
        }
        return formData.sizes[type.id] === 'not_sure' || formData.sizes[type.id];
      });
      
      if (!allSizesSelected) {
        setError('Please select sizes for all categories (or choose "I\'m not sure")');
        return;
      }
    }

    // For step 4 (gender), ensure gender is selected
    if (activeStep === 4 && !formData.gender) {
      setError('Please select your gender');
      return;
    }

    // For final step, handle registration
    if (activeStep === 8) {
      try {
        // Format the registration data
        const registrationData = {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          gender: formData.gender.toLowerCase(),
          birth_date: dayjs(formData.birthDate).format('YYYY-MM-DD'),
          age: dayjs().diff(dayjs(formData.birthDate), 'year'),
          sizes: formData.sizes || {},
          selected_styles: formData.selectedStyles.map(style => {
            const styleOption = styleOptions.find(opt => opt.name === style);
            return styleOption ? styleOption.id : null;
          }).filter(Boolean),
          favorite_colors: formData.favoriteColors || [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        // Get the temporary tokens
        const tempAccessToken = localStorage.getItem('temp_access_token');
        const tempRefreshToken = localStorage.getItem('temp_refresh_token');

        if (!tempAccessToken || !tempRefreshToken) {
          throw new Error('Verification tokens not found. Please verify your email again.');
        }

        // Complete the registration
        const response = await fetch(`${config.apiBaseUrl}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tempAccessToken}`
          },
          credentials: 'include',
          body: JSON.stringify(registrationData),
        });

        const data = await response.json();

        if (response.ok && data.status === 'success') {
          // Clean up temporary tokens
          localStorage.removeItem('temp_access_token');
          localStorage.removeItem('temp_refresh_token');
          
          // Store the final tokens and user data
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          localStorage.setItem('user', JSON.stringify(data.user));

          // Update auth context
          setCurrentUser(data.user);
          
          // Navigate to home
          navigate('/');
        } else {
          setError(data.message || 'Registration failed. Please try again.');
        }
      } catch (error) {
        console.error('Registration error:', error);
        setError('Failed to complete registration. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Proceed to next step if all validations pass
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateAge = (day, month, year) => {
    if (!day || !month || !year || year.length !== 4) return null;

    const birthDate = dayjs(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    if (!birthDate.isValid()) {
      setDateError('Please enter a valid date');
      return false;
    }

    const age = dayjs().diff(birthDate, 'year');
    
    if (age < 13) {
      setDateError('You must be at least 13 years old to use Lyke');
      return false;
    }
    
    if (age > 120) {
      setDateError('Please enter a valid birth date');
      return false;
    }

    setDateError('');
    return true;
  };

  const validateStep = () => {
    setError('');
    switch (activeStep) {
      case 0: // Welcome
        return true;
        
      case 1: // Name
        if (!formData.name.trim()) {
          setError('Please enter your name');
          return false;
        }
        return true;
        
      case 2: // Contact
        if (!formData.email.trim() || !formData.email.includes('@')) {
          setError('Please enter a valid email address');
          return false;
        }
        return true;
        
      case 3: // Verification
        if (!verificationCode.trim()) {
          setError('Please enter the verification code');
          return false;
        }
        return true;
        
      case 4: // Gender
        if (!formData.gender) {
          setError('Please select your gender');
          return false;
        }
        return true;
        
      case 5:
        if (!formData.birthDate) {
          setError('Please select your birth date');
          return false;
        }
        if (dateError) {
          setError(dateError);
          return false;
        }
        const age = dayjs().diff(dayjs(formData.birthDate), 'year');
        if (age < 13 || age > 120) {
          setError(age < 13 ? 'You must be at least 13 years old to use Lyke' : 'Please enter a valid birth date');
          return false;
        }
        return true;
        
      case 6: // Sizes
        const clothingTypesForGender = clothingTypes[formData.gender] || [];
        const allSizesSelected = clothingTypesForGender.every(type => {
          if (type.id === 'bottoms') {
            return (formData.sizes.bottoms?.waist === 'not_sure') || 
                   (formData.sizes.bottoms?.waist && formData.sizes.bottoms?.length);
          }
          return formData.sizes[type.id] === 'not_sure' || formData.sizes[type.id];
        });
        if (!allSizesSelected) {
          setError('Please select sizes for all categories (or choose "I\'m not sure")');
          return false;
        }
        return true;
        
      case 7:
        return true;
        
      case 8: // Final Welcome
        return true;
        
      default:
        return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only register on the final welcome screen (step 8)
    if (activeStep === 8) {
      setError('');
      setLoading(true);
      
      try {
        // Format the registration data
        const registrationData = {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          gender: formData.gender.toLowerCase(),
          birth_date: dayjs(formData.birthDate).format('YYYY-MM-DD'),
          age: dayjs().diff(dayjs(formData.birthDate), 'year'),
          sizes: formData.sizes || {},
          selected_styles: formData.selectedStyles.map(style => {
            const styleOption = styleOptions.find(opt => opt.name === style);
            return styleOption ? styleOption.id : null;
          }).filter(Boolean),
          favorite_colors: formData.favoriteColors || [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        // Get the temporary tokens
        const tempAccessToken = localStorage.getItem('temp_access_token');
        const tempRefreshToken = localStorage.getItem('temp_refresh_token');

        if (!tempAccessToken || !tempRefreshToken) {
          throw new Error('Verification tokens not found. Please verify your email again.');
        }

        // Complete the registration
        const response = await fetch(`${config.apiBaseUrl}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tempAccessToken}`
          },
          credentials: 'include',
          body: JSON.stringify(registrationData),
        });

        const data = await response.json();

        if (response.ok && data.status === 'success') {
          // Clean up temporary tokens
          localStorage.removeItem('temp_access_token');
          localStorage.removeItem('temp_refresh_token');
          
          // Store the final tokens and user data
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          localStorage.setItem('user', JSON.stringify(data.user));

          // Update auth context
          setCurrentUser(data.user);
          
          // Navigate to home
          navigate('/');
        } else {
          setError(data.message || 'Registration failed. Please try again.');
        }
      } catch (error) {
        console.error('Registration error:', error);
        setError('Failed to complete registration. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      handleNext();
    }
  };

  const toggleStyle = (styleId) => {
    setFormData(prev => ({
      ...prev,
      selectedStyles: prev.selectedStyles.includes(styleId)
        ? prev.selectedStyles.filter(id => id !== styleId)
        : [...prev.selectedStyles, styleId]
    }));
  };

  const toggleColor = (color) => {
    setFormData(prev => ({
      ...prev,
      favoriteColors: prev.favoriteColors.includes(color)
        ? prev.favoriteColors.filter(c => c !== color)
        : [...prev.favoriteColors, color]
    }));
  };

  const handleSizeChange = (type, value, subType = null) => {
    setFormData(prev => {
      if (value === 'not_sure') {
        if (type === 'bottoms' && prev.sizes[type]?.waist === 'not_sure') {
          return {
            ...prev,
            sizes: {
              ...prev.sizes,
              [type]: {}
            }
          };
        }
        if (prev.sizes[type] === 'not_sure') {
          return {
            ...prev,
            sizes: {
              ...prev.sizes,
              [type]: undefined
            }
          };
        }
        if (type === 'bottoms') {
          return {
            ...prev,
            sizes: {
              ...prev.sizes,
              [type]: { waist: 'not_sure', length: 'not_sure' }
            }
          };
        }
        return {
          ...prev,
          sizes: {
            ...prev.sizes,
            [type]: 'not_sure'
          }
        };
      }
      
      return {
        ...prev,
        sizes: {
          ...prev.sizes,
          [type]: subType 
            ? { ...(prev.sizes[type] || {}), [subType]: value }
            : value
        }
      };
    });
  };

  const handleSizeSystemChange = (event, newSystem) => {
    if (newSystem !== null) {
      setSizeSystem(newSystem);
    }
  };

  const getSizeOptions = (type, gender) => {
    const options = sizeOptions[gender][type];
    if (type === 'bottoms') return options;
    return options[sizeSystem];
  };

  const handleGenderToggle = (gender) => {
    setFormData(prev => ({
      ...prev,
      gender: prev.gender === gender ? '' : gender,
      // Clear size selections when gender changes
      sizes: {}
    }));
  };

  // Add verification code input props
  const verificationInputProps = {
    inputStyle: {
      fontFamily: 'monospace',
      margin: '4px',
      MozAppearance: 'textfield',
      width: '45px',
      borderRadius: '8px',
      fontSize: '24px',
      height: '45px',
      backgroundColor: 'white',
      border: '1px solid #ccc',
      textAlign: 'center',
      outline: 'none',
      color: '#333',
    },
    inputStyleInvalid: {
      fontFamily: 'monospace',
      margin: '4px',
      MozAppearance: 'textfield',
      width: '45px',
      borderRadius: '8px',
      fontSize: '24px',
      height: '45px',
      backgroundColor: 'white',
      border: '1px solid #ff0000',
      textAlign: 'center',
      outline: 'none',
      color: '#333',
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box
            sx={{
              height: '100vh',
              width: '100vw',
              position: 'fixed',
              top: 0,
              left: 0,
              backgroundImage: 'url("/onboarding_welcome.png")',
              backgroundSize: {
                xs: '300%', // Mobile
                sm: '300%', // Tablet
                md: '100%', // Laptop
                lg: '100%', // Desktop
                xl: '100%'  // Large Desktop
              },
              backgroundPosition: 'right center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '40px',
              color: 'white',
              textAlign: {
                xs: 'left',
                sm: 'left',
                md: 'center',
                lg: 'center',
                xl: 'center'
              }
            }}
          >
            <Box sx={{ maxWidth: '600px' }}>
            <Typography variant="h1" sx={{ fontSize: '3rem', mb: 2 }}>
              👋
            </Typography>
              <Typography variant="h3" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>
                Ready to shop <span style={{ textDecoration: 'line-through' }}>like</span> <i>lyke</i> never before?
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 6, color: 'white' }}>
                We promise you with most convenient experience for buying clothes..
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                maxWidth: '600px',
                width: '90%',
                position: 'fixed',
                bottom: 40,
                left: '50%',
                transform: 'translateX(-50%)',
                height: 56,
                borderRadius: 50,
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.light
                },
                fontSize: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
              endIcon={<ArrowForwardIcon />}
            >
              Get Started
            </Button>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h3" gutterBottom sx={{ color: 'black', fontWeight: 600 }}>
              Let's start with the basics..
            </Typography>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary', mb: 4 }}>
              What's your name?
            </Typography>
            <StyledTextField
              label="Name"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange('name')}
              required
              fullWidth
              autoFocus
              sx={{ backgroundColor: 'white' }}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h3" gutterBottom sx={{ color: 'black', fontWeight: 600 }}>
              Hey {formData.name} 🤝
            </Typography>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary', mb: 4 }}>
              What's your email address?
            </Typography>

            <Box sx={{ width: '100%' }}>
              <StyledTextField
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                fullWidth
                autoFocus
                error={!emailStatus.available}
                helperText={emailStatus.message}
                InputProps={{
                  endAdornment: emailStatus.checking && (
                    <CircularProgress size={20} />
                  ),
                }}
                sx={{ backgroundColor: 'white' }}
              />
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
              By continuing, you agree on the{' '}
              <Box
                component="span"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                terms and conditions
              </Box>
              {' '}of using Lyke.
            </Typography>

            <Typography variant="body2" color="textSecondary">
              Already have an account?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={onToggleAuth}
                sx={{ textDecoration: 'none', textDecoration: 'underline' }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h3" gutterBottom sx={{ color: 'black', fontWeight: 600 }}>
              Verify your email
            </Typography>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary', mb: 4 }}>
              We've sent a verification code to <strong>{formData.email}</strong>
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                '& > div': { // Target the react-code-input container
                  margin: '0 auto'
                }
              }}>
                <ReactCodeInput
                  type="text"
                  fields={6}
                  {...verificationInputProps}
                  value={verificationCode}
                  onChange={setVerificationCode}
                  autoFocus
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                {countdown > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Resend code in {countdown}s
                  </Typography>
                ) : (
                  <Button
                    onClick={handleSendVerificationCode}
                    disabled={loading}
                    sx={{ 
                      textTransform: 'none',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Didn't receive the code? Send again
                  </Button>
                )}
              </Box>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
              Please check your email inbox and spam folder. The code will expire in 10 minutes.
            </Typography>
          </Box>
        );
      case 4:
        return (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h3" gutterBottom sx={{ color: 'black', fontWeight: 600 }}>
              Great..
            </Typography>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary', mb: 4 }}>
              So what's your gender, {formData.name}?
            </Typography>
            <Box sx={{ mt: 3 }}>
              <GenderOption
                className={formData.gender === 'men' ? 'selected' : ''}
                onClick={() => handleGenderToggle('men')}
                fullWidth
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  👨 <Box sx={{ ml: 1 }}>Male</Box>
                </Box>
                <Box className="circle">
                  {formData.gender === 'men' && <CheckCircleOutlineIcon sx={{ height: '28px', width: '28px' }}/>}
                </Box>
              </GenderOption>
              <GenderOption
                className={formData.gender === 'women' ? 'selected' : ''}
                onClick={() => handleGenderToggle('women')}
                fullWidth
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  👩 <Box sx={{ ml: 1 }}>Female</Box>
                </Box>
                <Box className="circle">
                  {formData.gender === 'women' && <CheckCircleOutlineIcon sx={{ height: '28px', width: '28px' }} />}
                </Box>
              </GenderOption>
            </Box>
          </Box>
        );
      case 5:
        return (
          <>
            <Typography variant="h3" gutterBottom sx={{ color: 'black', fontWeight: 600 }}>
              {formData.gender === 'men' ? 'Nice Buddy' : 'Nice Gal'}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
              When were you born?
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              mt: 3 
            }}>
              <StyledDateField
                placeholder="DD"
                value={formData.day || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (/^\d{0,2}$/.test(value) && parseInt(value) <= 31)) {
                    setFormData(prev => ({
                      ...prev,
                      day: value,
                      birthDate: value && prev.month && prev.year ? 
                        `${prev.year}-${prev.month.padStart(2, '0')}-${value.padStart(2, '0')}` : null
                    }));
                    validateAge(value, formData.month, formData.year);
                  }
                }}
                inputProps={{ maxLength: 2 }}
                sx={{ width: '30%' }}
                error={!!dateError}
              />
              <StyledDateField
                placeholder="MM"
                value={formData.month || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (/^\d{0,2}$/.test(value) && parseInt(value) <= 12)) {
                    setFormData(prev => ({
                      ...prev,
                      month: value,
                      birthDate: value && prev.day && prev.year ? 
                        `${prev.year}-${value.padStart(2, '0')}-${prev.day.padStart(2, '0')}` : null
                    }));
                    validateAge(formData.day, value, formData.year);
                  }
                }}
                inputProps={{ maxLength: 2 }}
                sx={{ width: '30%' }}
                error={!!dateError}
              />
              <StyledDateField
                placeholder="YYYY"
                value={formData.year || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d{0,4}$/.test(value)) {
                    setFormData(prev => ({
                      ...prev,
                      year: value,
                      birthDate: value && prev.month && prev.day ? 
                        `${value}-${prev.month.padStart(2, '0')}-${prev.day.padStart(2, '0')}` : null
                    }));
                    if (value.length === 4) {
                      validateAge(formData.day, formData.month, value);
                    }
                  }
                }}
                inputProps={{ maxLength: 4 }}
                sx={{ width: '40%' }}
                error={!!dateError}
              />
            </Box>
              <Typography variant="body2" sx={{ color: 'error.main', mt: 2 }}>
                {dateError}
              </Typography>
          </>
        );
      case 6:
        return (
          <Box sx={{ maxWidth: 600 }}>
            <Typography variant="h3" gutterBottom >
              What are your usual sizes?
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom mb={3}>
              Please select sizes for all categories. Don't worry if you're not sure - you can select "I'm not sure" for any category.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <SizeToggle
                value={sizeSystem}
                exclusive
                onChange={handleSizeSystemChange}
                aria-label="size system"
              >
                <ToggleButton value="us">US Sizes</ToggleButton>
                <Box sx={{ width: '20px' }} />
                <ToggleButton value="eu">EU Sizes</ToggleButton>
              </SizeToggle>
            </Box>
            <Box sx={{ mt: 3, mb: 10 }}>
              {(clothingTypes[formData.gender] || []).map((type) => (
                <Box key={type.id} sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ 
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    {type.label}
                    <Typography 
                      component="span" 
                      sx={{ 
                        color: 'error.main',
                        fontSize: '0.875rem'
                      }}
                    >
                      *
                    </Typography>
                  </Typography>
                  {type.id === 'bottoms' ? (
                    <>
                      <NotSureButton
                        className={formData.sizes.bottoms?.waist === 'not_sure' ? 'selected' : ''}
                        onClick={() => handleSizeChange('bottoms', 'not_sure')}
                        variant="outlined"
                        fullWidth
                        sx={{
                          border: 'none',
                          backgroundColor: theme.palette.primary.brandLight,
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.light,
                            border: 'none'
                          },
                          '&.selected': {
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            border: 'none'
                          }
                        }}
                      >
                        I'm not sure about my pants size
                      </NotSureButton>
                      {formData.sizes.bottoms?.waist !== 'not_sure' && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom>
                              Waist
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {getSizeOptions('bottoms', formData.gender).waist[sizeSystem].map((size) => (
                                <Button
                                  key={size}
                                  variant={formData.sizes.bottoms?.waist === size ? 'contained' : 'outlined'}
                                  onClick={() => handleSizeChange('bottoms', size, 'waist')}
                                  sx={{ 
                                    minWidth: '60px',
                                    height: '40px',
                                    borderRadius: 2,
                                    border: 'none',
                                    backgroundColor: formData.sizes.bottoms?.waist === size ? 
                                      theme.palette.primary.main : theme.palette.primary.brandLight,
                                    color: formData.sizes.bottoms?.waist === size ? 
                                      'white' : theme.palette.primary.main,
                                    '&:hover': {
                                      backgroundColor: formData.sizes.bottoms?.waist === size ? 
                                        theme.palette.primary.main : theme.palette.primary.light,
                                      border: 'none'
                                    }
                                  }}
                                >
                                  {size}
                                </Button>
                              ))}
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" gutterBottom>
                              Length
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {getSizeOptions('bottoms', formData.gender).length.map((size) => (
                                <Button
                                  key={size}
                                  variant={formData.sizes.bottoms?.length === size ? 'contained' : 'outlined'}
                                  onClick={() => handleSizeChange('bottoms', size, 'length')}
                                  sx={{ 
                                    minWidth: '60px',
                                    height: '40px',
                                    borderRadius: 2,
                                    border: 'none',
                                    backgroundColor: formData.sizes.bottoms?.length === size ? 
                                      theme.palette.primary.main : theme.palette.primary.brandLight,
                                    color: formData.sizes.bottoms?.length === size ? 
                                      'white' : theme.palette.primary.main,
                                    '&:hover': {
                                      backgroundColor: formData.sizes.bottoms?.length === size ? 
                                        theme.palette.primary.main : theme.palette.primary.light,
                                      border: 'none'
                                    }
                                  }}
                                >
                                  {size}
                                </Button>
                              ))}
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <>
                      <NotSureButton
                        className={formData.sizes[type.id] === 'not_sure' ? 'selected' : ''}
                        onClick={() => handleSizeChange(type.id, 'not_sure')}
                        variant="outlined"
                        fullWidth
                        sx={{
                          border: 'none',
                          backgroundColor: theme.palette.primary.brandLight,
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.light,
                            border: 'none'
                          },
                          '&.selected': {
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            border: 'none'
                          }
                        }}
                      >
                        I'm not sure about my {type.label.toLowerCase()} size
                      </NotSureButton>
                      {formData.sizes[type.id] !== 'not_sure' && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {getSizeOptions(type.id, formData.gender).map((size) => (
                            <Button
                              key={size}
                              variant={formData.sizes[type.id] === size ? 'contained' : 'outlined'}
                              onClick={() => handleSizeChange(type.id, size)}
                              sx={{ 
                                minWidth: '60px',
                                height: '40px',
                                borderRadius: 2,
                                border: 'none',
                                backgroundColor: formData.sizes[type.id] === size ? 
                                  theme.palette.primary.main : theme.palette.primary.brandLight,
                                color: formData.sizes[type.id] === size ? 
                                  'white' : theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: formData.sizes[type.id] === size ? 
                                    theme.palette.primary.main : theme.palette.primary.light,
                                  border: 'none'
                                }
                              }}
                            >
                              {size}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        );
      case 7:
        const currentOutfit = outfitOptions[formData.gender][currentOutfitIndex];
        
        // Guard against accessing undefined outfit
        if (!currentOutfit) {
          handleNext();
          return null;
        }
        
        return (
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <Typography variant="h3" gutterBottom sx={{ color: 'black', fontWeight: 600 }}>
              Will you wear this?
            </Typography>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary', mb: 4 }}>
              Let's play a little game to know your taste. You'll see different styles - just give each one a 👍 or 👎
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 2
            }}>
              <StyleActionButton
                onClick={() => handleOutfitResponse(false)}
              >
                👎
              </StyleActionButton>

              <OutfitImage>
                <img
                  src={currentOutfit.image}
                  alt={currentOutfit.style}
                />
              </OutfitImage>

              <StyleActionButton
                onClick={() => handleOutfitResponse(true)}
              >
                👍
              </StyleActionButton>
            </Box>
          </Box>
        );
      case 8:
        return (
          <Box sx={{ 
            maxWidth: '600px',
            height: 'calc(100vh - 200px)', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            width: '100%',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
              🎉
            </Typography>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
              Welcome {formData.name}!
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 6, color: 'text.secondary' }}>
              You can enjoy the app now. We promise you with an unforgettable experience..
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  const isStepButtonDisabled = () => {
    switch (activeStep) {
      case 0:
        return false;
      case 1:
        return !formData.name.trim();
      case 2:
        return !formData.email || !formData.email.includes('@') || !emailStatus.available || emailStatus.checking;
      case 3:
        return !verificationCode.trim();
      case 4:
        return !formData.gender;
      case 5:
        return !formData.birthDate;
      case 6:
        const clothingTypesForGender = clothingTypes[formData.gender] || [];
        return !clothingTypesForGender.every(type => {
          if (type.id === 'bottoms') {
            return (formData.sizes.bottoms?.waist === 'not_sure') || 
                   (formData.sizes.bottoms?.waist && formData.sizes.bottoms?.length);
          }
          return formData.sizes[type.id] === 'not_sure' || formData.sizes[type.id];
        });
      case 7:
        return false;
      case 8:
        return false;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ paddingTop: activeStep === 0 ? 0 : 0 }}>
      <FormContainer sx={{ display: activeStep === 0 ? 'none' : 'block' }}>
        <Box sx={{ mb: 4 }}>
          {activeStep !== 0 && activeStep !== 8 && (
            <Button
              onClick={handleBack}
              sx={{
                mb: 2,
                color: 'black',
                minWidth: '40px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                padding: 0,
                '&:hover': {
                  backgroundColor: theme.palette.primary.brandLight,
                  color: theme.palette.primary.main,
                }
              }}
            >
              <ArrowForwardIcon sx={{ transform: 'rotate(180deg)' }} />
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {steps.slice(0, steps.length - 2).map((_, index) => {
              const isStyleStep = index === 6;
              const progressWidth = isStyleStep && formData.gender ? 
                `${((currentOutfitIndex + 1) / outfitOptions[formData.gender].length) * 100}%` : 
                '0%';
              
              return (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    '--progress': progressWidth,
                  }}
                >
                  <ProgressBar
                    isactive={index === activeStep - 1}
                    iscompleted={index < activeStep - 1}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          
          {getStepContent(activeStep)}
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: 'auto',
            position: 'fixed',
            bottom: 40,
            left: 0,
            right: 0,
            padding: '0 20px',
            maxWidth: 600,
            margin: '0 auto',
            visibility: activeStep === 0 ? 'hidden' : 'visible' 
          }}>
            <Box sx={{ width: '100%' }}>
              <StyledButton
                variant="contained"
                type="submit"
                disabled={loading || isStepButtonDisabled()}
                fullWidth
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  ...(activeStep === 7 && {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    }
                  })
                }}
              >
                {activeStep === 8 ? "Let's go!" : activeStep === 7 ? "Skip the game" : "Proceed"} <ArrowForwardIcon />
              </StyledButton>
            </Box>
          </Box>
        </Box>
        
        {activeStep === 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Already have an account?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={onToggleAuth}
                sx={{ textDecoration: 'underline' }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        )}
      </FormContainer>
      {activeStep === 0 && getStepContent(0)}
    </Box>
  );
}

export default Register; 